<template>
  <PageBreadcrumb
    :crumbs="[{
      link: '/aste/',
      label: 'Gestione Aste'
    }, {
      link: '/aste/' + auctionId + '/lotti',
      label: 'Gestione Lotti Asta' + auction.name
    }, {
      label: 'Crea Lotto per ' + auction.name
    }]"
  />
  
  <form autocomplete="off">
    <InputText
      v-model="form.code"
      :error-fields="errorFields"
      name="code"
      :submitted="submitted"
    >
      Codice
    </InputText>

    <InputInteger
      v-model="form.quantity"
      :error-fields="errorFields"
      name="quantity"
      :submitted="submitted"
    >
      Quantità<br><span class="text-sm">(in tonnellate)</span>
    </InputInteger>

    <ProductInputSelect
      v-model="form.productUuid"
      :category-uuid="auction.categoryUuid"
      :error-fields="errorFields"
      name="productUuid"
      :submitted="submitted"
    />

    <WasteCodeInputSelect
      v-model="form.wasteCodeUuid"
      :error-fields="errorFields"
      name="wasteCodeUuid"
      :submitted="submitted"
    />

    <InputDecimal
      v-model="form.startPrice"
      :error-fields="errorFields"
      name="startPrice"
      :submitted="submitted"
    >
      Prezzo Iniziale<br><span class="text-sm">(in euro €)</span>
    </InputDecimal>

    <InputDecimal
      v-model="form.bidPrice"
      :error-fields="errorFields"
      name="bidPrice"
      :submitted="submitted"
    >
      Rilancio<br><span class="text-sm">(in euro €)</span>
    </InputDecimal>

    <h4 class="uppercase text-sm mb-4 mt-8 font-bold">
      Informazioni addizionali
    </h4>

    <InputSelect
      v-model="form.attributes.tipoPagamento"
      :error-fields="errorFields"
      name="tipoPagamento"
      :options="tipoPagamentoValues"
      :submitted="submitted"
    >
      Condizioni di Pagamento
    </InputSelect>

    <InputSelect
      v-model="form.attributes.territorioInteresse"
      :error-fields="errorFields"
      name="territorioInteresse"
      :options="territorioInteresseValues"
      :submitted="submitted"
    >
      Territorio di Interesse
    </InputSelect>

    <InputSelect
      v-model="form.attributes.provenienzaProdotto"
      :error-fields="errorFields"
      name="provenienzaProdotto"
      :options="provenienzaProdottoValues"
      :submitted="submitted"
    >
      Provenienza del Prodotto
    </InputSelect>

    <InputText
      v-model="form.attributes.dataDisponibilitaMateriale"
      :error-fields="errorFields"
      name="dataDisponibilitaMateriale"
      :submitted="submitted"
    >
      Data Disponibilità Materiale
    </InputText>

    <InputSelect
      v-model="form.attributes.tipologiaProdotto"
      :error-fields="errorFields"
      name="tipologiaProdotto"
      :options="tipologiaProdottoValues"
      :submitted="submitted"
    >
      Tipologia di Prodotto
    </InputSelect>

    <InputSelect
      v-model="form.attributes.statoFisico"
      :error-fields="errorFields"
      name="statoFisico"
      :options="statoFisicoValues"
      :submitted="submitted"
    >
      Stato del Prodotto
    </InputSelect>

    <InputSelect
      v-model="form.attributes.imballaggio"
      :error-fields="errorFields"
      name="imballaggio"
      :options="imballaggioValues"
      :submitted="submitted"
    >
      Imballaggio
    </InputSelect>

    <InputSelect
      v-model="form.attributes.qualitaMateriale"
      :error-fields="errorFields"
      name="qualitaMateriale"
      :options="qualitaMaterialeValues"
      :submitted="submitted"
    >
      Qualità Materiale
    </InputSelect>

    <InputTextarea
      v-model="form.notes"
      :error-fields="errorFields"
      name="notes"
      :submitted="submitted"
    >
      Note
    </InputTextarea>

    <div class="flex mb-4 mt-8">
      <h4 class="uppercase text-sm flex-grow font-bold">
        Depositi
      </h4>
      <div>
        <ErrorRow
          :error-fields="errorFields"
          name="warehouses"
          :submitted="submitted"
        />
      </div>
    </div>

    <div
      v-for="warehouse, index in form.warehouses"
      :key="'lot-warehouses-' + index"
      class="flex mb-2 text-sm border-b items-start"
    >
      <div class="flex flex-grow mr-2">
        <div class="w-1/2 mr-2">
          <InputSelect
            v-model="warehouse.selectionSiteUuid"
            :error-fields="errorFields"
            :name="'warehouses.' + index + '.selectionSiteUuid'"
            :options="selectionSites"
            :submitted="submitted"
          >
            Centro di Selezione
          </InputSelect>
        </div>
        <div class="w-1/2 ml-2">
          <InputInteger
            v-model="warehouse.quantity"
            :error-fields="errorFields"
            :name="'warehouses.' + index + '.quantity'"
            :submitted="submitted"
          >
            Quantità
            <br><span class="text-xs">(in tonnellate)</span>
          </InputInteger>
        </div>
      </div>
      <div class="flex ml-2">
        <ActionButton @click.prevent="removeWarehouse(index)">
          <MinusCircleIcon class="h-5 w-5 inline-block" />
        </ActionButton>
      </div>
    </div>

    <div class="flex justify-end mt-4 items-center">
      <div>
        <ActionButton @click.prevent="addWarehouse">
          <PlusCircleIcon class="h-5 w-5 inline-block" /> Aggiungi Deposito
        </ActionButton>
      </div>
    </div>

    <SubmitButtons
      :create-and-back="createAndBack"
      :create-and-stay="createAndStay"
      :mode="form.uuid ? 'update' : 'create'"
      :pending="pending"
      :update-and-back="updateAndBack"
      :update-and-stay="updateAndStay"
    />
  </form>
</template>

<script setup>
import {
  MinusCircleIcon,
  PlusCircleIcon} from "@heroicons/vue/24/outline";
import _ from "underscore";
import { reactive, ref, watch } from "vue";
import { useRoute } from 'vue-router';

import ActionButton from "@/components/ActionButton.vue";
import ErrorRow from "@/components/form/ErrorRow.vue";
import InputDecimal from "@/components/form/InputDecimal.vue";
import InputInteger from "@/components/form/InputInteger.vue";
import InputSelect from "@/components/form/InputSelect.vue";
import InputText from "@/components/form/InputText.vue";
import InputTextarea from "@/components/form/InputTextarea.vue";
import ProductInputSelect from "@/components/form/ProductInputSelect.vue";
import SubmitButtons from "@/components/form/SubmitButtons.vue";
import WasteCodeInputSelect from "@/components/form/WasteCodeInputSelect.vue";
import PageBreadcrumb from "@/components/PageBreadcrumb.vue";
import useAxios from "@/composables/axios";
import useForm from "@/composables/form";
import useLotAttributes from '@/composables/lotAttributes.js'
import { useNotificationsStore } from "@/stores/notifications";

const props = defineProps({
  id: {
    required: false,
    type: String,
    default: null
  },
});

const { 
  imballaggioValues, 
  provenienzaProdottoValues, 
  qualitaMaterialeValues,
  statoFisicoValues,
  territorioInteresseValues,
  tipologiaProdottoValues,
  tipoPagamentoValues
} = useLotAttributes();

const axios = useAxios();
const route = useRoute();
const notificationStore = useNotificationsStore();

const auctionId = route.params.auctionId
const auctionResp = await axios.get('/auction/' + auctionId);
const auction = ref(auctionResp.data);

const selectionSiteResp = await axios.get('/selection-site/list')
const selectionSites = selectionSiteResp.data.map((selectionSite) => {
  return {
    id: selectionSite.uuid,
    label: selectionSite.displayName
  }
})

const lotExists = ref(false);

let form = reactive({
  code: "",
  quantity: null,
  productUuid: null,
  wasteCodeUuid: null,
  startPrice: null,
  bidPrice: null,
  notes: '',
  attributes: {
    tipoPagamento: null,
    territorioInteresse: null,
    provenienzaProdotto: null,
    dataDisponibilitaMateriale: null,
    tipologiaProdotto: null,
    statoFisico: null,
    imballaggio: null,
    qualitaMateriale: null
  },
  warehouses: []
});

if (props.id) {
  const resp = await axios.get("/lot/" + props.id);
  if(!resp.data.attributes) {
    resp.data.attributes = {};
  }
  form = reactive(resp.data);
  form.startPrice = parseFloat(form.startPrice);
  form.bidPrice = parseFloat(form.bidPrice);
}

watch(form, () => {
  lotExists.value = false
})

const rules = {
  code: {
    type: "string",
    max: 25,
    required: true,
    validator: (rule, value) => {
      const errors = [];
      
      if (!value) {
        errors.push('Campo obbligatorio');
      }
      
      if (value && value.length > 25) {
        errors.push('Il codice deve essere lungo al massimo 25 caratteri');
      }
      
      if (lotExists.value) {
        errors.push('Codice lotto già esistente');
      }
      
      return errors.length ? errors : true;
    }
  },
  quantity: {
    required: true,
    type: "integer",
    min: 0,
    max: 999999
  },
  productUuid: {
    type: "string",
    required: true,
  },
  wasteCodeUuid: {
    type: "string",
    required: true,
  },
  startPrice: {
    required: true,
    type: "integer",
    min: -999999,
    max: 999999,
    transform(value) {
      return parseInt(value, 10);
    },
  },
  bidPrice: {
    required: true,
    type: "integer",
    min: 0,
    max: 999999,
    transform(value) {
      return parseInt(value, 10);
    },
  },
  warehouses: {
    required: true,
    type: "array",
    validator: (rule, value, callback, source, options) => {
      const quantitySum = source.warehouses ? source.warehouses.reduce((sum, warehouse) => sum + warehouse.quantity, 0) : null;
      if (quantitySum != source.quantity) {
        return ['Quantità non corrispondenti'];
      }
      const selectionSiteUuid = _.pluck(source.warehouses, 'selectionSiteUuid');
      if (selectionSiteUuid.length !== _.uniq(selectionSiteUuid).length) {
        return ['Centri di Selezione ripetuti'];
      }
      return true;
    },
    defaultField: {
      type: 'object',
      required: true,
      fields: {
        quantity: {
          type: "integer",
          min: 1,
          max: 999999,
          transform(value) {
            return parseInt(value, 10);
          },
        },
        selectionSiteUuid: {
          type: "string",
          required: true
        }
      }
    }
  }
};

const { submitted, pending, errorFields, createAndBack, createAndStay, updateAndBack, updateAndStay } = useForm(form, 'lot/' + auctionId, rules, {
  create: 'Lotto creato con successo',
  update: 'Lotto modificato con successo'
}, '/aste/' + auctionId + '/lotti', () => {
  form.code = ''
  // form.productUuid = null
  // form.wasteCodeUuid = null
  // form.startPrice = ''
  // form.bidPrice = ''
  // form.notes = ''

  // form.attributes.tipoPagamento = null
  // form.attributes.territorioInteresse = null
  // form.attributes.provenienzaProdotto = null
  // form.attributes.dataDisponibilitaMateriale = null
  // form.attributes.tipologiaProdotto = null
  // form.attributes.statoFisico = null
  // form.attributes.imballaggio = null
  // form.attributes.qualitaMateriale = null

  form.warehouses = []

}, false, (e) => {
  if(e.response.data.code == 'lot-code-exists') {
    lotExists.value = true;
    notificationStore.pushError('Ci sono alcuni errori')
  }
})

const removeWarehouse = (index) => {
  form.warehouses.splice(index, 1)
}

const addWarehouse = () => {
  form.warehouses.push({
    selectionSiteUuid: null,
    quantity: null
  })
}

const addError = () => {
  errorFields.value['code'] = [{
    field: 'code',
    fieldValue: form.code,
    message: 'Codice lotto duplicato'
  }];
  submitted.value = true
}
</script>
